import { useState } from "react";
import {
  Button,
  Card,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  useAdminDeleteAccountMutation,
  useAdminGetAccountsQuery,
  useAdminGetRequestsQuery,
  useAdminProcessRequestMutation,
  useAdminSaveAccountMutation,
} from "../../../store/service/paymentService";

const emptyAccount = {
  type: "bank",
  label: "BANK ACCOUNT",
  bank_name: "",
  account_number: "",
  ifsc_code: "",
  account_name: "",
  upi_id: "",
  whatsapp_number: "",
  min_amount: 300,
  max_amount: 10000,
  instructions: "",
  is_active: true,
  sort_order: 0,
};

const PaymentAdmin = () => {
  const [status, setStatus] = useState("pending");
  const [accountModal, setAccountModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [noteModal, setNoteModal] = useState(null);

  const { data: accData, isLoading: accLoading } = useAdminGetAccountsQuery();
  const { data: reqData, isLoading: reqLoading, refetch } = useAdminGetRequestsQuery(status);
  const [saveAccount, { isLoading: saving }] = useAdminSaveAccountMutation();
  const [deleteAccount] = useAdminDeleteAccountMutation();
  const [processRequest, { isLoading: processing }] = useAdminProcessRequestMutation();

  const accounts = accData?.data || [];
  const requests = reqData?.data || [];

  const openAdd = () => {
    setEditing(null);
    form.setFieldsValue(emptyAccount);
    setAccountModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      type: row.type,
      label: row.label,
      bank_name: row.bankName,
      account_number: row.accountNumber,
      ifsc_code: row.ifscCode,
      account_name: row.accountName,
      upi_id: row.upiId,
      whatsapp_number: row.whatsappNumber,
      min_amount: row.minAmount,
      max_amount: row.maxAmount,
      instructions: row.instructions,
      is_active: row.isActive,
      sort_order: row.sortOrder,
    });
    setAccountModal(true);
  };

  const onSaveAccount = async () => {
    try {
      const values = await form.validateFields();
      const body = { ...values, id: editing?.id };
      const res = await saveAccount(body).unwrap();
      message.success(res.message || "Saved");
      setAccountModal(false);
    } catch (err) {
      if (err?.data?.message) message.error(err.data.message);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteAccount({ id }).unwrap();
      message.success("Deleted");
    } catch (err) {
      message.error(err?.data?.message || "Delete failed");
    }
  };

  const onProcess = async (id, action, admin_note = "") => {
    try {
      const res = await processRequest({ id, action, admin_note }).unwrap();
      message.success(res.message);
      setNoteModal(null);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Failed");
    }
  };

  const accountColumns = [
    { title: "Label", dataIndex: "label", key: "label" },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "A/C No", dataIndex: "accountNumber", key: "accountNumber" },
    { title: "IFSC", dataIndex: "ifscCode", key: "ifscCode" },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Action",
      key: "action",
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button size="small" danger onClick={() => onDelete(row.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const requestColumns = [
    { title: "ID", dataIndex: "id", width: 60 },
    { title: "User", dataIndex: "userId", width: 80 },
    {
      title: "Type",
      dataIndex: "type",
      render: (t) => (
        <Tag color={t === "deposit" ? "blue" : "orange"}>{t}</Tag>
      ),
    },
    { title: "Amount", dataIndex: "amount" },
    { title: "UTR", dataIndex: "utr" },
    {
      title: "Bank (user)",
      key: "ubank",
      render: (_, r) =>
        r.type === "withdrawal"
          ? `${r.userAccountName || ""} / ${r.userAccountNumber || ""}`
          : r.accountLabel || "—",
    },
    {
      title: "Proof",
      dataIndex: "proofUrl",
      render: (url) =>
        url ? (
          <Image src={url} width={48} height={48} style={{ objectFit: "cover" }} />
        ) : (
          "—"
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => {
        const color =
          s === "approved" ? "green" : s === "rejected" ? "red" : "gold";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Action",
      key: "act",
      render: (_, row) =>
        row.status === "pending" ? (
          <Space>
            <Button
              type="primary"
              size="small"
              loading={processing}
              onClick={() => onProcess(row.id, "approve")}>
              Approve
            </Button>
            <Button
              size="small"
              danger
              onClick={() => setNoteModal({ row, action: "reject" })}>
              Reject
            </Button>
          </Space>
        ) : (
          row.adminNote || "—"
        ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <h2>Payment — Deposit / Withdrawal</h2>

      <Tabs
        items={[
          {
            key: "requests",
            label: "Requests",
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }}>
                  <Select
                    value={status}
                    onChange={setStatus}
                    options={[
                      { value: "pending", label: "Pending" },
                      { value: "approved", label: "Approved" },
                      { value: "rejected", label: "Rejected" },
                      { value: "all", label: "All" },
                    ]}
                  />
                  <Button onClick={() => refetch()}>Refresh</Button>
                </Space>
                <Table
                  rowKey="id"
                  loading={reqLoading}
                  columns={requestColumns}
                  dataSource={requests}
                  scroll={{ x: 900 }}
                  pagination={{ pageSize: 20 }}
                />
              </Card>
            ),
          },
          {
            key: "accounts",
            label: "Bank Accounts",
            children: (
              <Card>
                <Button type="primary" onClick={openAdd} style={{ marginBottom: 12 }}>
                  Add Account
                </Button>
                <Table
                  rowKey="id"
                  loading={accLoading}
                  columns={accountColumns}
                  dataSource={accounts}
                  pagination={false}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={editing ? "Edit Account" : "Add Account"}
        open={accountModal}
        onCancel={() => setAccountModal(false)}
        onOk={onSaveAccount}
        confirmLoading={saving}
        width={560}>
        <Form form={form} layout="vertical" initialValues={emptyAccount}>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "bank", label: "Bank" },
                { value: "upi", label: "UPI" },
                { value: "whatsapp", label: "WhatsApp" },
              ]}
            />
          </Form.Item>
          <Form.Item name="label" label="Label" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="bank_name" label="Bank Name">
            <Input />
          </Form.Item>
          <Form.Item name="account_number" label="Account Number">
            <Input />
          </Form.Item>
          <Form.Item name="ifsc_code" label="IFSC">
            <Input />
          </Form.Item>
          <Form.Item name="account_name" label="Account Name">
            <Input />
          </Form.Item>
          <Form.Item name="upi_id" label="UPI ID">
            <Input />
          </Form.Item>
          <Form.Item name="whatsapp_number" label="WhatsApp">
            <Input />
          </Form.Item>
          <Space>
            <Form.Item name="min_amount" label="Min Amount">
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item name="max_amount" label="Max Amount">
              <InputNumber min={1} />
            </Form.Item>
          </Space>
          <Form.Item name="instructions" label="Instructions">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort Order">
            <InputNumber />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Reject request"
        open={!!noteModal}
        onCancel={() => setNoteModal(null)}
        onOk={() => {
          const note = document.getElementById("reject-note")?.value || "";
          onProcess(noteModal.row.id, noteModal.action, note);
        }}>
        <Input.TextArea id="reject-note" rows={3} placeholder="Reason (optional)" />
      </Modal>
    </div>
  );
};

export default PaymentAdmin;
